import { FC, memo, useCallback, useMemo } from 'react'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { TableCell, Txt } from 'src/components/atoms'
import { TableSortableTxt, TableSortType } from 'src/components/molecules'

import { useAuthority } from 'src/hooks'
import { ProjectEntity, SelectableProjectEntity } from 'src/types'

export type ProjectSortItemType = keyof Pick<
  ProjectEntity,
  | 'name'
  | 'status'
  | 'prefecture'
  | 'creator'
  | 'createdAt'
  | 'updatedAt'
  | 'structureCount'
  | 'roomCount'
  | 'planCount'
  | 'averagePriceRange'
  | 'purchaserCount'
  | 'applicationCount'
>

type Props = {
  selectableProjects: SelectableProjectEntity[]
  sortItem: ProjectSortItemType
  sortType: TableSortType
  onSelectItem: (newItem: ProjectSortItemType) => void
  onClickAllSelect: () => void
  className?: string
}

export const ProjectListHead: FC<Props> = memo(
  ({ selectableProjects, sortItem, sortType, onSelectItem, onClickAllSelect, className }) => {
    const { canAssignProject } = useAuthority()

    const isAllSelected = useMemo(
      () => selectableProjects.every(project => project.isSelected),
      [selectableProjects]
    )
    const isSomeSelected = useMemo(
      () => selectableProjects.some(project => project.isSelected),
      [selectableProjects]
    )
    const selectedProjectCount = useMemo(
      () => selectableProjects.filter(project => project.isSelected).length,
      [selectableProjects]
    )

    return (
      <div className={className}>
        <div className="tw-bg-surface-secondary tw-py-s-1">
          <div className="tw-flex tw-items-center tw-flex-nowrap">
            {canAssignProject && (
              <TableCell width={4}>
                <div className="tw-flex tw-flex-nowrap tw-items-center tw-ml-s0">
                  <div role="presentation" onClick={onClickAllSelect}>
                    <Checkbox
                      nonce={undefined}
                      checked={isSomeSelected}
                      indeterminate={!isAllSelected && isSomeSelected}
                    />
                  </div>
                  <Txt sizeType="l" className="tw-font-bold tw-text-roovPrimary-default tw-ml-s-2">
                    {selectedProjectCount}
                  </Txt>
                </div>
              </TableCell>
            )}
            <TableCell width={4} align="right" />
            <TableCell width={17}>
              <TableSortableTxt
                text="Projects"
                isActive={sortItem === 'name'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('name'), [onSelectItem])}
                className="tw-ml-s-1"
              />
            </TableCell>
            <TableCell width={7}>
              <TableSortableTxt
                text="Status"
                isActive={sortItem === 'status'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('status'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={6}>
              <TableSortableTxt
                text="エリア"
                isActive={sortItem === 'prefecture'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('prefecture'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={8}>
              <TableSortableTxt
                text="作成者"
                isActive={sortItem === 'creator'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('creator'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={6}>
              <TableSortableTxt
                text="作成日"
                isActive={sortItem === 'createdAt'}
                sortType={sortType}
                onSelect={() => onSelectItem('createdAt')}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={8}>
              <TableSortableTxt
                text="最終更新日"
                isActive={sortItem === 'updatedAt'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('updatedAt'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={6} align="right">
              <TableSortableTxt
                text="総戸数"
                isActive={sortItem === 'roomCount'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('roomCount'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={6} align="right">
              <TableSortableTxt
                text="お客様数"
                isActive={sortItem === 'purchaserCount'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('purchaserCount'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell width={7} align="right">
              <TableSortableTxt
                text="申込・成約数"
                isActive={sortItem === 'applicationCount'}
                sortType={sortType}
                onSelect={useCallback(() => onSelectItem('applicationCount'), [onSelectItem])}
                className="tw-mr-s-1"
              />
            </TableCell>
            <TableCell className="tw-w-full">
              <Txt sizeType="s" className="tw-text-standardText-secondary tw-ml-s-1">
                ROOV compass
              </Txt>
            </TableCell>
          </div>
        </div>
      </div>
    )
  }
)
